import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Clock, User, Filter, RefreshCw, Shield, DollarSign, CheckCircle2, XCircle, Settings, UserX, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ACTION_ICONS = {
  login: User,
  logout: User,
  wallet_adjustment: DollarSign,
  deposit_approve: CheckCircle2,
  deposit_reject: XCircle,
  withdrawal_approve: CheckCircle2,
  withdrawal_reject: XCircle,
  user_update: Settings,
  disable_user: UserX,
  reset_password: KeyRound,
  change_role: Shield,
  permission_grant: Shield,
  permission_deny: Shield,
  payment_method_create: Settings,
  payment_method_update: Settings,
  payment_method_delete: Settings,
  settings_change: Settings,
};

const ACTION_TONE = {
  deposit_approve: 'green', withdrawal_approve: 'green',
  deposit_reject: 'red', withdrawal_reject: 'red', disable_user: 'red',
  permission_grant: 'amber', permission_deny: 'red',
};

const fmtTime = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString([], { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(ts); }
};

const humanize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function StaffActivityPage() {
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ action: '', admin_id: '', target_type: '', search: '' });
  const [expanded, setExpanded] = useState({});

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.admin_id) params.admin_id = filters.admin_id;
      if (filters.target_type) params.target_type = filters.target_type;
      params.limit = 200;
      const res = await api.get('/api/admin/audit', { params });
      const items = res.data?.data?.logs || res.data?.logs || [];
      setLogs(items);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const at = await api.get('/api/admin/audit/action-types');
        setActionTypes(at.data?.data?.items || []);
      } catch {}
    })();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchAll, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.action, filters.admin_id, filters.target_type]);

  const filteredLogs = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(l =>
      (l.admin_username || '').toLowerCase().includes(q)
      || (l.action || '').toLowerCase().includes(q)
      || (l.target_type || '').toLowerCase().includes(q)
      || (l.target_id || '').toLowerCase().includes(q)
    );
  }, [logs, filters.search]);

  const uniqueAdmins = useMemo(() => {
    const map = new Map();
    logs.forEach(l => { if (l.admin_id) map.set(l.admin_id, l.admin_username || l.admin_id); });
    return Array.from(map.entries());
  }, [logs]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="staff-activity-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-[#D4AF37]" /> Staff Activity Audit</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {filteredLogs.length} event{filteredLogs.length === 1 ? '' : 's'} — tenant-scoped
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" className="border-white/10 rounded-full" disabled={refreshing} data-testid="audit-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
        <div className="relative md:col-span-2">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            value={filters.search}
            onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            placeholder="Search by admin, action, target…"
            className="pl-9 bg-[#0B0E14] border-white/10 rounded-full"
            data-testid="audit-search"
          />
        </div>
        <select
          value={filters.action}
          onChange={(e) => setFilters(p => ({ ...p, action: e.target.value }))}
          className="bg-[#0B0E14] border border-white/10 rounded-full px-3 py-2 text-sm text-white"
          data-testid="audit-action-filter"
        >
          <option value="">All actions</option>
          {actionTypes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select
          value={filters.admin_id}
          onChange={(e) => setFilters(p => ({ ...p, admin_id: e.target.value }))}
          className="bg-[#0B0E14] border border-white/10 rounded-full px-3 py-2 text-sm text-white"
          data-testid="audit-admin-filter"
        >
          <option value="">All staff</option>
          {uniqueAdmins.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {/* Activity list */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-xl border border-white/10">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-600" />
          <p className="text-gray-400 text-sm">No audit events match your filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log, idx) => {
            const Icon = ACTION_ICONS[log.action] || Settings;
            const tone = ACTION_TONE[log.action] || 'gray';
            const toneClass = {
              green:  'bg-green-500/15 text-green-400 border-green-500/25',
              red:    'bg-red-500/15 text-red-400 border-red-500/25',
              amber:  'bg-amber-500/15 text-amber-300 border-amber-500/25',
              gray:   'bg-white/5 text-gray-300 border-white/10',
            }[tone];
            const key = log.id || `${log.created_at}-${idx}`;
            const isOpen = !!expanded[key];
            const hasDetails = log.details && Object.keys(log.details).length > 0;

            return (
              <div
                key={key}
                className="glass-card rounded-xl border border-white/10 overflow-hidden"
                data-testid={`audit-row-${idx}`}
              >
                <button
                  onClick={() => hasDetails && setExpanded(p => ({ ...p, [key]: !isOpen }))}
                  className={`w-full text-left p-3 flex items-center gap-3 ${hasDetails ? 'hover:bg-white/5' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${toneClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{humanize(log.action)}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 border border-white/5">
                        {log.admin_role || 'staff'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      by <span className="text-gray-200 font-medium">{log.admin_username}</span>
                      {log.target_type && <> on <span className="text-gray-200">{log.target_type}</span></>}
                      {log.target_id && <> <code className="text-[10px] text-gray-500">({String(log.target_id).slice(0, 8)}…)</code></>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-gray-500">{fmtTime(log.created_at)}</p>
                    {hasDetails && (
                      <p className="text-[10px] text-[#D4AF37] mt-0.5">
                        {isOpen ? 'Hide details ▴' : 'Show details ▾'}
                      </p>
                    )}
                  </div>
                </button>
                {isOpen && hasDetails && (
                  <div className="px-3 pb-3 pt-0 border-t border-white/5" data-testid={`audit-details-${idx}`}>
                    <pre className="text-[11px] bg-black/40 p-2.5 rounded-md overflow-auto max-h-48 text-gray-300 font-mono">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
